/**
 * SiRiX Style Sidebar Panel System
 * Vertical tabs on the left edge that expand into full panels on hover
 * Matches the SiRiX Broker Manager design
 * Updated to use WebSocket for real-time price feeds
 */

import { useState, useEffect, useRef } from 'react';
import { useWebSocket } from '@/context/WebSocketContext';
import { riskAPI } from '@/services/api';
import { 
  Search, 
  ChevronDown, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  RefreshCw,
  X,
  Wifi,
  WifiOff
} from 'lucide-react';

// ============== MARKET RATES PANEL ==============
// Note: Forex, crypto, and stock symbols - using standard format for WebSocket
const DEFAULT_INSTRUMENTS = [
  // Forex pairs
  { symbol: 'EUR/USD', name: 'EUR/USD', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'GBP/USD', name: 'GBP/USD', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'USD/JPY', name: 'USD/JPY', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'USD/CHF', name: 'USD/CHF', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'AUD/USD', name: 'AUD/USD', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'NZD/USD', name: 'NZD/USD', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'USD/CAD', name: 'USD/CAD', type: 'forex', group: 'Currency Pairs' },
  { symbol: 'EUR/GBP', name: 'EUR/GBP', type: 'forex', group: 'Currency Pairs' },
  // Crypto
  { symbol: 'BTC/USD', name: 'Bitcoin', type: 'crypto', group: 'Crypto' },
  { symbol: 'ETH/USD', name: 'Ethereum', type: 'crypto', group: 'Crypto' },
  { symbol: 'XRP/USD', name: 'Ripple', type: 'crypto', group: 'Crypto' },
  { symbol: 'BNB/USD', name: 'BNB', type: 'crypto', group: 'Crypto' },
  { symbol: 'SOL/USD', name: 'Solana', type: 'crypto', group: 'Crypto' },
  { symbol: 'ADA/USD', name: 'Cardano', type: 'crypto', group: 'Crypto' },
  { symbol: 'DOGE/USD', name: 'Dogecoin', type: 'crypto', group: 'Crypto' },
  // Commodities
  { symbol: 'XAU/USD', name: 'Gold', type: 'commodity', group: 'Commodities' },
  { symbol: 'XAG/USD', name: 'Silver', type: 'commodity', group: 'Commodities' },
  // BIST Stocks
  { symbol: 'THYAO', name: 'THYAO', type: 'stock', group: 'Borsa Istanbul' },
  { symbol: 'GARAN', name: 'GARAN', type: 'stock', group: 'Borsa Istanbul' },
  { symbol: 'AKBNK', name: 'AKBNK', type: 'stock', group: 'Borsa Istanbul' },
];

const GROUPS = ['All groups', 'Currency Pairs', 'Crypto', 'Commodities', 'Borsa Istanbul'];

const MarketRatesPanel = ({ onClose }) => {
  const symbols = DEFAULT_INSTRUMENTS.map(i => i.symbol);
  const { prices, subscribe, isConnected } = useWebSocket();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('All groups');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [localPrices, setLocalPrices] = useState({});
  
  // Known prices for fallback display when WebSocket is not connected
  const FALLBACK_PRICES = {
    'EUR/USD': { bid: 1.0842, ask: 1.0845, change_pct: 0.15 },
    'GBP/USD': { bid: 1.2634, ask: 1.2637, change_pct: -0.08 },
    'USD/JPY': { bid: 149.52, ask: 149.55, change_pct: 0.22 },
    'USD/CHF': { bid: 0.8845, ask: 0.8848, change_pct: 0.05 },
    'AUD/USD': { bid: 0.6523, ask: 0.6526, change_pct: -0.12 },
    'NZD/USD': { bid: 0.5987, ask: 0.5990, change_pct: 0.08 },
    'USD/CAD': { bid: 1.3567, ask: 1.3570, change_pct: -0.05 },
    'EUR/GBP': { bid: 0.8578, ask: 0.8581, change_pct: 0.10 },
    'BTC/USD': { bid: 87500, ask: 87550, change_pct: 1.25 },
    'ETH/USD': { bid: 4850, ask: 4855, change_pct: 0.85 },
    'XRP/USD': { bid: 2.85, ask: 2.86, change_pct: -0.45 },
    'BNB/USD': { bid: 720, ask: 722, change_pct: 0.35 },
    'SOL/USD': { bid: 195, ask: 196, change_pct: 1.15 },
    'ADA/USD': { bid: 1.25, ask: 1.26, change_pct: -0.25 },
    'DOGE/USD': { bid: 0.42, ask: 0.421, change_pct: 2.15 },
    'XAU/USD': { bid: 4447, ask: 4448.50, change_pct: 0.65 },
    'XAG/USD': { bid: 69.30, ask: 69.35, change_pct: 0.45 },
    'THYAO': { bid: 385, ask: 386.50, change_pct: 0.85 },
    'GARAN': { bid: 145, ask: 145.50, change_pct: -0.35 },
    'AKBNK': { bid: 72, ask: 72.25, change_pct: 0.15 },
  };
  
  // Use fallback prices if WebSocket not connected
  useEffect(() => {
    if (!isConnected || Object.keys(prices).length === 0) {
      setLocalPrices(FALLBACK_PRICES);
    }
  }, [isConnected, prices]);
  
  // Subscribe to all symbols on mount
  useEffect(() => {
    if (isConnected) {
      subscribe(symbols);
    }
  }, [isConnected, subscribe, symbols]);

  const formatPrice = (price, symbol) => {
    if (!price) return '--';
    if (symbol.includes('JPY')) return price.toFixed(2);
    if (['BTCUSD', 'ETHUSD', 'XAUUSD'].includes(symbol)) {
      return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (price < 1) return price.toFixed(5);
    if (price < 100) return price.toFixed(2);
    return price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatChange = (change) => {
    if (!change && change !== 0) return '0.00%';
    const sign = change >= 0 ? '' : '';
    return `${sign}${change.toFixed(2)}%`;
  };

  const filteredInstruments = DEFAULT_INSTRUMENTS.filter(inst => {
    const matchesSearch = inst.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesGroup = selectedGroup === 'All groups' || inst.group === selectedGroup;
    return matchesSearch && matchesGroup;
  });

  return (
    <div className="h-full flex flex-col bg-white" data-testid="market-rates-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <h2 className="text-[#00a99d] font-bold text-lg tracking-wide">MARKET RATES</h2>
        <button 
          onClick={onClose} 
          className="p-1 hover:bg-gray-200 rounded text-gray-500"
          data-testid="close-market-rates"
        >
          <X size={18} />
        </button>
      </div>

      {/* Search & Filter Row */}
      <div className="flex items-center gap-2 p-2 border-b">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-7 pr-2 py-1.5 text-sm border rounded focus:outline-none focus:border-[#00a99d]"
            data-testid="market-rates-search"
          />
        </div>
        <div className="relative">
          <button
            onClick={() => setShowGroupDropdown(!showGroupDropdown)}
            className="flex items-center gap-1 px-2 py-1.5 text-sm border rounded hover:bg-gray-50 min-w-[100px] justify-between"
            data-testid="market-rates-group-filter"
          >
            <span className="truncate text-xs">{selectedGroup}</span>
            <ChevronDown size={14} />
          </button>
          {showGroupDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded shadow-lg z-50 min-w-[120px]">
              {GROUPS.map(group => (
                <div
                  key={group}
                  className="px-3 py-1.5 text-sm hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setSelectedGroup(group);
                    setShowGroupDropdown(false);
                  }}
                >
                  {group}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-[1fr_70px_70px_65px] gap-1 px-2 py-1.5 border-b bg-gray-50 text-xs font-medium text-gray-600">
        <div className="flex items-center gap-1">
          Instrument <TrendingUp size={10} />
        </div>
        <div className="text-right">Bid</div>
        <div className="text-right">Ask</div>
        <div className="text-right">Daily<br/>Change</div>
      </div>

      {/* Rates List */}
      <div className="flex-1 overflow-y-auto">
        {!isConnected && Object.keys(prices).length === 0 && Object.keys(localPrices).length === 0 ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Connecting...
          </div>
        ) : (
          filteredInstruments.map(inst => {
            // Try multiple symbol formats for price lookup
            const symbolWithSlash = inst.symbol;
            const symbolWithoutSlash = inst.symbol.replace('/', '');
            // Check WebSocket prices first, then fallback to local prices
            const priceData = prices[symbolWithSlash] || prices[symbolWithoutSlash] || 
                             localPrices[symbolWithSlash] || localPrices[symbolWithoutSlash] || null;
            
            const change = priceData?.change_pct || 0;
            const isPositive = change > 0;
            const isNegative = change < 0;
            
            // Get type icon color
            const typeColor = inst.type === 'crypto' ? 'text-orange-500' : 
                             inst.type === 'forex' ? 'text-blue-500' : 
                             inst.type === 'commodity' ? 'text-yellow-500' : 'text-green-500';
            
            return (
              <div 
                key={inst.symbol}
                className="grid grid-cols-[1fr_70px_70px_65px] gap-1 px-2 py-1.5 border-b border-gray-100 hover:bg-gray-50 items-center text-sm"
                data-testid={`market-rate-${inst.symbol}`}
              >
                {/* Instrument */}
                <div className="flex items-center gap-1.5 min-w-0">
                  <TrendingUp size={12} className={typeColor} />
                  <span className="font-medium text-gray-800 truncate text-xs">{inst.name}</span>
                </div>
                
                {/* Bid */}
                <div className="text-right font-mono text-xs tabular-nums">
                  {formatPrice(priceData?.bid, inst.symbol)}
                </div>
                
                {/* Ask */}
                <div className="text-right font-mono text-xs tabular-nums">
                  {formatPrice(priceData?.ask, inst.symbol)}
                </div>
                
                {/* Daily Change */}
                <div className={`text-right font-mono text-xs tabular-nums ${
                  isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-gray-600'
                }`}>
                  {formatChange(change)}
                </div>
              </div>
            );
          })
        )}
      </div>
      
      {/* Connection Status */}
      <div className={`flex items-center justify-center gap-1 py-1 text-xs border-t ${
        isConnected ? 'text-green-600 bg-green-50' : 'text-orange-600 bg-orange-50'
      }`}>
        {isConnected ? <Wifi size={12} /> : <WifiOff size={12} />}
        {isConnected ? 'Live' : 'Reconnecting...'}
      </div>
    </div>
  );
};

// ============== MARGIN CALL PANEL ==============
const MARGIN_THRESHOLDS = {
  danger: 100,
  warning: 150,
  caution: 200,
};

const MarginCallPanel = ({ onClose }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAlerts = async () => {
    try {
      const data = await riskAPI.getMarginAlerts();
      setAlerts(data || []);
    } catch (error) {
      // Use mock data for demo
      setAlerts([
        { 
          client_id: "1", 
          name: "John Smith", 
          account_id: "10544293",
          equity: 1250.00,
          margin: 1500.00,
          margin_level: 83.33,
        },
        { 
          client_id: "2", 
          name: "Emma Wilson", 
          account_id: "10544294",
          equity: 2800.00,
          margin: 2000.00,
          margin_level: 140.00,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getAlertStyle = (marginLevel) => {
    if (marginLevel <= MARGIN_THRESHOLDS.danger) return 'bg-red-50 border-red-200 text-red-700';
    if (marginLevel <= MARGIN_THRESHOLDS.warning) return 'bg-orange-50 border-orange-200 text-orange-700';
    return 'bg-yellow-50 border-yellow-200 text-yellow-700';
  };

  const sortedAlerts = [...alerts].sort((a, b) => a.margin_level - b.margin_level);
  const dangerCount = alerts.filter(a => a.margin_level <= MARGIN_THRESHOLDS.danger).length;

  return (
    <div className="h-full flex flex-col bg-white" data-testid="margin-call-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`w-5 h-5 ${dangerCount > 0 ? 'text-red-500' : 'text-orange-500'}`} />
          <h2 className="text-gray-800 font-bold text-base">MARGIN CALL</h2>
          {dangerCount > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{dangerCount}</span>
          )}
        </div>
        <button 
          onClick={onClose} 
          className="p-1 hover:bg-gray-200 rounded text-gray-500"
          data-testid="close-margin-call"
        >
          <X size={18} />
        </button>
      </div>

      {/* Alerts List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Loading...
          </div>
        ) : sortedAlerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No margin alerts</p>
          </div>
        ) : (
          <div className="divide-y">
            {sortedAlerts.map(alert => (
              <div 
                key={alert.client_id}
                className={`p-3 ${getAlertStyle(alert.margin_level)}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{alert.name}</span>
                  <span className="font-mono font-bold text-sm">
                    {alert.margin_level.toFixed(1)}%
                  </span>
                </div>
                <div className="text-xs opacity-80">
                  #{alert.account_id} | Equity: ${alert.equity?.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ============== MAIN SIDEBAR COMPONENT ==============
const SirixSidebarPanel = () => {
  const [activePanel, setActivePanel] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const panelRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = (panel) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActivePanel(panel);
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setActivePanel(null);
    }, 300);
  };

  const handlePanelMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handlePanelMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsHovering(false);
      setActivePanel(null);
    }, 300);
  };

  const closePanel = () => {
    setActivePanel(null);
    setIsHovering(false);
  };

  return (
    <>
      {/* Vertical Tab Bar */}
      <div className="fixed left-0 top-[48px] bottom-[24px] w-7 bg-[#4a5568] flex flex-col z-[100]">
        {/* Market Rates Tab */}
        <div
          className={`flex items-center justify-center py-4 cursor-pointer transition-colors ${
            activePanel === 'market' ? 'bg-[#00a99d]' : 'hover:bg-[#5a6578]'
          }`}
          onMouseEnter={() => handleMouseEnter('market')}
          onMouseLeave={handleMouseLeave}
          data-testid="market-rates-tab"
        >
          <span 
            className="text-white text-[10px] font-bold tracking-wider"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            MARKET RATES
          </span>
        </div>

        {/* Margin Call Tab */}
        <div
          className={`flex items-center justify-center py-4 cursor-pointer transition-colors ${
            activePanel === 'margin' ? 'bg-orange-500' : 'hover:bg-[#5a6578]'
          }`}
          onMouseEnter={() => handleMouseEnter('margin')}
          onMouseLeave={handleMouseLeave}
          data-testid="margin-call-tab"
        >
          <span 
            className="text-white text-[10px] font-bold tracking-wider"
            style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
          >
            MARGIN CALL
          </span>
        </div>
      </div>

      {/* Expandable Panel */}
      {activePanel && (
        <div
          ref={panelRef}
          className="fixed left-7 top-[48px] bottom-[24px] w-[340px] bg-white shadow-xl border-r z-[99] animate-slideIn"
          onMouseEnter={handlePanelMouseEnter}
          onMouseLeave={handlePanelMouseLeave}
          style={{
            animation: 'slideIn 0.2s ease-out'
          }}
        >
          {activePanel === 'market' && <MarketRatesPanel onClose={closePanel} />}
          {activePanel === 'margin' && <MarginCallPanel onClose={closePanel} />}
        </div>
      )}

      {/* Animation keyframes */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
};

export default SirixSidebarPanel;
